import { all, fork, call, put, takeLatest } from 'redux-saga/effects';
import {
  Types as EnterpriseTypes,
  Creators as EnterpriseActions,
} from '../ducks/Enterprise';
import api from '../../util/Api';
import * as Sentry from '@sentry/react';

const getApiError = (error, fallbackMessage) => {
  const responseData = error?.response?.data;

  if (typeof responseData === 'string') {
    return { message: responseData };
  }

  if (Array.isArray(responseData)) {
    const responseMessage = responseData.find(item => item?.message)?.message;
    if (responseMessage) {
      return { message: responseMessage };
    }
  }

  if (responseData?.message) {
    return { ...responseData, message: responseData.message };
  }

  return { message: error?.message || fallbackMessage };
};

function* getEnterprises({ payload }) {
  try {
    const { status, data } = yield call(api.get, '/businesses', {
      params: payload,
    });
    if (status === 200) {
      yield put(EnterpriseActions.getEnterprisesSuccess(data));
    }
    else {
      Sentry.captureMessage('getEnterprises failed: status not 200', {
        level: 'error',
        extra: { payload, status, data },
      });
    }
  } catch (error) {
    Sentry.captureException(error, { extra: { payload } });
    yield put(
      EnterpriseActions.getEnterprisesError('Error listing projects')
    );
  }
}

function* postEnterprise({ payload }) {

  try {
    const { status } = yield call(api.post, '/businesses', payload, {
      headers: {
        ...(payload instanceof FormData
          ? { 'Content-Type': 'multipart/form-data' }
          : {}),
      },
    });
    if (status === 200) {
      yield put(EnterpriseActions.postEnterpriseSuccess());
    }
    else {
      Sentry.captureMessage('postEnterprise failed: status not 200', {
        level: 'error',
        extra: { payload, status },
      });
    }
  } catch (error) {
    Sentry.captureException(error, { extra: { payload } });
    yield put(EnterpriseActions.postEnterpriseError(getApiError(error, 'Error registering unit')));
  }
}

function* getEnterpriseById({ payload }) {
  const { id, params, edit } = payload;
  try {
    const { status, data } = yield call(api.get, `/businesses/${id}`, {
      params,
    });
    if (status === 200) {
      if (edit && data?.approved !== 'APPROVED') {
        yield put(
          EnterpriseActions.getEnterpriseByIdError(
            'development not found'
          )
        );
      }
      yield put(EnterpriseActions.getEnterpriseByIdSuccess(data));
    }
    else {
      Sentry.captureMessage('getEnterpriseById failed: status not 200', {
        level: 'error',
        extra: { payload, status, data },
      });
    }
  } catch (error) {
    Sentry.captureException(error, { extra: { payload } });
    yield put(
      EnterpriseActions.getEnterpriseByIdError('development not found')
    );
  }
}

function* putEnterprise({ payload }) {
  const { id, data } = payload;
  try {
    const { status } = yield call(api.put, `/businesses/${id}`, data, {
      headers: {
        ...(data instanceof FormData
          ? { 'Content-Type': 'multipart/form-data' }
          : {}),
      },
    });
    if (status === 200) {
      yield put(EnterpriseActions.putEnterpriseSuccess());
    }
    else {
      Sentry.captureMessage('putEnterprise failed: status not 200', {
        level: 'error',
        extra: { payload, status },
      });
    }
  } catch (error) {
    Sentry.captureException(error, { extra: { payload } });
    yield put(
      EnterpriseActions.putEnterpriseError(getApiError(error, 'Error editing unit'))
    );
  }
}

function* deleteEnterprise({ payload }) {
  try {
    const { status } = yield call(api.delete, `/businesses/${payload}`);
    if (status === 200) {
      yield put(EnterpriseActions.deleteEnterpriseSuccess());
    }
    else {
      Sentry.captureMessage('deleteEnterprise failed: status not 200', {
        level: 'error',
        extra: { payload, status },
      });
    }
  } catch (error) {
    Sentry.captureException(error, { extra: { payload } });
    yield put(
      EnterpriseActions.deleteEnterpriseError('Error deleting unit')
    );
  }
}

function* approveEnterprise({ payload }) {
  try {
    const { status } = yield call(api.post, `/businesses/${payload}/approve`);
    if (status === 200) {
      yield put(EnterpriseActions.approveEnterpriseSuccess());
    }
    else {
      Sentry.captureMessage('approveEnterprise failed: status not 200', {
        level: 'error',
        extra: { payload, status },
      });
    }
  } catch (error) {
    Sentry.captureException(error, { extra: { payload } });
    yield put(
      EnterpriseActions.approveEnterpriseError(error?.response?.data?.message)
    );
  }
}

function* refuseEnterprise({ payload }) {
  const { id, data } = payload;
  try {
    const { status } = yield call(api.post, `/businesses/${id}/refuse`, data);
    if (status === 200) {
      yield put(EnterpriseActions.refuseEnterpriseSuccess());
    }
    else {
      Sentry.captureMessage('refuseEnterprise failed: status not 200', {
        level: 'error',
        extra: { payload, status },
      });
    }
  } catch (error) {
    Sentry.captureException(error, { extra: { payload } });
    yield put(EnterpriseActions.refuseEnterpriseError());
  }
}

function* getEnterprisesWatcher() {
  yield takeLatest(EnterpriseTypes.GET_ENTERPRISES, getEnterprises);
}

function* postEnterpriseWatcher() {
  yield takeLatest(EnterpriseTypes.POST_ENTERPRISE, postEnterprise);
}

function* getEnterpriseByIdWatcher() {
  yield takeLatest(EnterpriseTypes.GET_ENTERPRISE_BY_ID, getEnterpriseById);
}

function* putEnterpriseWatcher() {
  yield takeLatest(EnterpriseTypes.PUT_ENTERPRISE, putEnterprise);
}

function* deleteEnterpriseWatcher() {
  yield takeLatest(EnterpriseTypes.DELETE_ENTERPRISE, deleteEnterprise);
}

function* approveEnterpriseWatcher() {
  yield takeLatest(EnterpriseTypes.APPROVE_ENTERPRISE, approveEnterprise);
}

function* refuseEnterpriseWatcher() {
  yield takeLatest(EnterpriseTypes.REFUSE_ENTERPRISE, refuseEnterprise);
}

export default function* rootSaga() {
  yield all([
    fork(getEnterprisesWatcher),
    fork(postEnterpriseWatcher),
    fork(getEnterpriseByIdWatcher),
    fork(putEnterpriseWatcher),
    fork(deleteEnterpriseWatcher),
    fork(approveEnterpriseWatcher),
    fork(refuseEnterpriseWatcher),
  ]);
}
